import React, { useCallback, useEffect, useState } from 'react'
import { useForm, FormProvider, set } from 'react-hook-form'
import moment from 'moment'

import { Card, CollapseSearch } from 'components'
import { useAuth, useLoading, useModal, useMovement, usePersistForm } from 'hooks'
import { MovimentacaoMensalServices } from 'services/Supplier/MovMensal'
import { FORMAT, FUNCTIONS, SPACING } from 'utils'
import { PERMITIONS } from 'permitions'
import { yup, yupResolver } from 'utils/yup'

import { CamposVisaoTotal, ContratosResponsePesquisaMovimentacao } from '../../types'
import { ResponseGetParametrizacaoEfeitvo } from '../../types'
import {
  FilterMovPesquisaFornecedor,
  TableMovPesquisaFornecedor
} from './components'
import { CollapseSearchV2 } from 'components/CollapseV2'
import styled from 'styled-components'
import { number, string } from 'yup'
import Legendas from './legendas'
import { reportsDatabase } from 'services/database/reportsDatabase'
import Cre from 'pages/NGT/CRE/AnalyzeCreContract'

export interface IPesquisaMovFornecedor {
  handleChangeTab: (_competencia: string) => void
  parametrizacoes: ResponseGetParametrizacaoEfeitvo
  handleIsExpanded: (contratoId: number) => boolean
  setSelectedContratoID: (contratoId: number) => void
}

export interface IForm {
  CdContrato: string
  CompetenciaInicio: string
  CompetenciaFim: string
  NmFornecedor: string
}

const scheme = yup.object({
  CompetenciaInicio: yup.string()
    .daterange({
      period: 'begin',
      compare: 'CompetenciaFim',
      message: 'Competência Inicio não pode ser maior que competência fim',
    }).date().nullable(),
  CompetenciaFim: yup.string()
    .daterange({
      period: 'end',
      compare: 'CompetenciaInicio',
      message: 'Competência fim não pode ser menor que competência início',
    }).date().nullable()
})

// Wrapper que desabilita apenas o botão, não o collapse
const ButtonDisabledWrapper = styled.div<{ isDisabled: boolean }>`
  position: relative;
  
  ${({ isDisabled }) => isDisabled && `
    /* Overlay que cobre apenas a área do botão (lado direito) */
    &::before {
      content: '';
      position: absolute;
      top: 20px;
      right: 15px;
      width: 227px;
      height: 62.14px;
      background: transparent;
      cursor: not-allowed;
      z-index: 999;
      pointer-events: all;
    }
  `}
`;

const PesquisaMovFornecedor: React.FC<IPesquisaMovFornecedor> = ({
  handleChangeTab,
  parametrizacoes,
  handleIsExpanded,
  setSelectedContratoID
}) => {
  const { setSelectedMovement, resetContext, isAnalista } = useMovement()
  const { user /*allDatadescripted*/ } = useAuth()
  const { setLoading } = useLoading()
  const { showModal } = useModal()

  const formMethods = useForm<IForm>({
    resolver: yupResolver(scheme),
    defaultValues: {
      CdContrato: '',
      CompetenciaFim: null,
      CompetenciaInicio: null,
      NmFornecedor: ''
    },
  })
  usePersistForm('movimentacao-mensal-fornecedor-form', {
    setValue: formMethods.setValue,
    watch: formMethods.watch
  })



  const [listaMovimentacoes, setListaMovimentacoes] = useState<
    ContratosResponsePesquisaMovimentacao[] | undefined
  >()
  const [statusMovimentacaoFornecedor, setStatusMovimentacaoFornecedor] = useState<{ flMovValida?: boolean } | undefined>(undefined)
  const [contraparte, SetContraparte] = useState(false)

  useEffect(() => {
    const getMovimentacoes = async () => {
      setLoading('loading')
      try {
        const response =
          await MovimentacaoMensalServices().getPesquisaMovimentacao({
            idUsuario: user.IdUsuario,
            ...FUNCTIONS.filterObject(formMethods.getValues(), Boolean),
            ...(user.PerfilUsuarioId === PERMITIONS.CSNCONTRAPARTE && {
              isContraparte: true,
            })
          })
        // LOG 1: DADOS COMPLETOS DA RESPOSTA DA API
        //console.log('=== RESPOSTA COMPLETA DA API ===', response.data.entity);
        if (user.PerfilUsuarioId === PERMITIONS.CSNCONTRAPARTE) {
          SetContraparte(true)
        }
        if (response.status !== 200)
          throw new Error('Erro buscar movimentações')
        setListaMovimentacoes(response.data.entity)
        setLoading('idle')
      } catch (error: any) {
        setLoading('idle')
        showModal({
          hideButtonCancel: true,
          message:
            error.mensagem ||
            error.message ||
            'Não foi possivel obter movimentações',
          txtButtonConfirm: 'Fechar',
          type: 'error'
        })
      }
    }
    getMovimentacoes()
  }, [])

  const getCompetencia = (): string => {
    const today = Number(moment().format('DD'))

    // ✅ Verificação de segurança
    if (!parametrizacoes) {
      console.warn('Parametrizações não carregadas, usando competência padrão');
      // Retorna mês anterior como padrão seguro
      return moment().subtract(1, 'month').format('MM/YYYY');
    }

    let diaFim = Number(parametrizacoes.dsFimFechamentoFornecedorContraparte)

    if (isAnalista) {
      diaFim = Number(parametrizacoes?.dsFimFechamentoAnalista)
    }

    return today <= diaFim
      ? moment().subtract(1, 'month').format('MM/YYYY')
      : moment().subtract(1, 'month').format('MM/YYYY')
  }

  const handleStatusFornecedor = (status: CamposVisaoTotal) => {
    setStatusMovimentacaoFornecedor({
      flMovValida: status.flMovValida
    })
  }

  const handleClickCollapse = (data: ContratosResponsePesquisaMovimentacao) => {
    // LOG 3: DADOS DO ITEM CLICADO
    /*console.log('=== ITEM CLICADO ===', {
      contratoId: data.contratoId,
      statusMovimentacaoFornecedor: data.statusMovimentacaoFornecedor,
      cdContrato: data.cdContrato,
      nmEmpresaFornecedor: data.nmEmpresaFornecedor,
      dtVigenciaInicio: data.dtVigenciaInicio,
      dtVigenciaFim: data.dtVigenciaFim,
      fornecedorId: data.fornecedorId,
      encerrado: data.encerrado,
      cre: data.cre
    });*/

    // Verifica se o botão está desabilitado antes de executar a ação
    const buttonDisabled = isButtonDisabled(data?.encerrado, data?.flCompAtual, data?.statusMovimentacaoFornecedor, data?.cre);

    if (buttonDisabled) {
      //console.log('Botão desabilitado - contrato encerrado (encerrado=1):', data?.cdContrato);
      return;
    }

    setSelectedContratoID(data.contratoId);
    const compAtual = getCompetencia()

    handleChangeTab(compAtual)
    setSelectedMovement({
      cdContrato: data.cdContrato,
      cdContratoPrincipal: data.cdContratoPrincipal,
      contratoId: data.contratoId,
      dtVigenciaFim: data.dtVigenciaFim,
      dtVigenciaInicio: data.dtVigenciaInicio,
      flCompAtual: true,
      fornecedorId: data.fornecedorId,
      mesAno: compAtual,
      nmEmpresaFornecedor: data.nmEmpresaFornecedor,
      nmEmpresaFornecedorPrincipal: data.nmEmpresaFornecedorPrincipal,
      txObjetoContrato: data.txObjetoContrato
    })
  }

  const handleSearch = () => {
    setLoading('loading')
    const form = formMethods.getValues()

    MovimentacaoMensalServices()
      .getPesquisaMovimentacao({
        idUsuario: user.IdUsuario,
        ...(form.CdContrato && { CdContrato: form.CdContrato }),
        ...(form.CompetenciaInicio && {
          CompetenciaInicio: moment(form.CompetenciaInicio).toDate()
        }),
        ...(form.CompetenciaFim && {
          CompetenciaFim: moment(form.CompetenciaFim).toDate()
        }),
        ...(form.NmFornecedor && { NomeFornecedor: form.NmFornecedor }),
        ...(user.PerfilUsuarioId === PERMITIONS.CSNCONTRAPARTE && {
          isContraparte: true
        })
      })
      .then((response) => {
        if (response.status !== 200) {
          throw new Error(
            response.statusText ||
            'Não foi possível obter os resultados da pesquisa'
          )
        }
        setListaMovimentacoes(response.data.entity)
        setLoading('idle')
      })
      .catch((error) => {
        setLoading('idle')
        showModal({
          hideButtonCancel: true,
          message: 'Não foi possivel obter o dados da pesquisa',
          txtButtonConfirm: 'OK',
          type: 'error'
        })
      })
  }

  const handleClickCompAnterior = (
    competencia: string,
    data: ContratosResponsePesquisaMovimentacao
  ) => {
    setSelectedContratoID(data.contratoId);
    //SELECT COMP FROM TABLE
    handleChangeTab(competencia)
    setSelectedMovement({
      cdContrato: data.cdContrato,
      cdContratoPrincipal: data.cdContratoPrincipal,
      contratoId: data.contratoId,
      dtVigenciaFim: data.dtVigenciaFim,
      dtVigenciaInicio: data.dtVigenciaInicio,
      flCompAtual: false,
      fornecedorId: data.fornecedorId,
      mesAno: competencia,
      nmEmpresaFornecedor: data.nmEmpresaFornecedor,
      nmEmpresaFornecedorPrincipal: data.nmEmpresaFornecedorPrincipal,
      txObjetoContrato: data.txObjetoContrato
    })
  }

  useEffect(resetContext, [])

  const getStatusColor = (status: string, isDisabled = false) => {
    console.log('Cor do status:', status); // Log da cor que vem

    // Se o botão está desabilitado, retorna cores cinza
    if (isDisabled) {
      return {
        buttonColor: '#CCCCCC',
        borderColor: '#999999',
        colorName: 'Gray'
      };
    }

    switch (status) {
      case 'Concluída':
        return { buttonColor: '#81C784', borderColor: '#388E3C', colorName: 'Green' }
      case 'Em andamento':
        return { buttonColor: '#FFEB3B', borderColor: '#FFD700', colorName: 'Yellow' }
      case 'Não iniciada':
        return { buttonColor: '#DB5461', borderColor: '#D32F2F', colorName: 'Red' };
      case 'Sem mobilização concluída':
        return { buttonColor: '#81C784', borderColor: '#388E3C', colorName: 'Green' }
      default:
        return { buttonColor: '#DB5461', borderColor: '#D32F2F', colorName: 'Red' };
    }
  }

  // Função para verificar se o botão deve ser desabilitado
  const isButtonDisabled = (encerrado?: number, flCompAtual?: boolean, statusMovimentacaoFornecedor?: string, cre?: number) => {
    const compAtual = getCompetencia();

    //console.log('=== COMPETENCIA ATUAL ===', compAtual);
    //console.log('=== ENCERRADO ===', encerrado);
    //console.log('=== COMPETENCIA ATUAL ===', flCompAtual);
    //console.log('=== Status Movimentacao Fornecedor ===', statusMovimentacaoFornecedor)
    //console.log('=== CRE ===', cre);

    //Se Contrato for encerrado e estiver na competência atual
    if (encerrado === 1 && flCompAtual === true) {
      return false; // Habilita o botão
    }

    //Se Contrato for N/A e estiver na competência atual
    else if (cre === 8 && flCompAtual === true) {
      return false; // Habilita o botão
    }

    //Se Contrato for N/A e não estiver na competência atual
    else if (cre === 8 && flCompAtual === false) {
      return true; // Desabilita o botão
    }

    // Caso contrário, mantém a lógica atual: desabilita se encerrado = 1
    return encerrado === 1;
  }

  const handleExpanded = useCallback(async (expanded, el) => {
    if (!expanded) {
      await reportsDatabase.expandedCardsMovMensal.delete(el?.contratoId);
      return;
    }
    await reportsDatabase.expandedCardsMovMensal.add({ id: el?.contratoId })
  }, [])

  return (
    <Card
      marginLeft={SPACING.spacing20}
      marginRight={SPACING.spacing20}
      marginBottom={SPACING.spacing100}
      padding={SPACING.spacing20}
      boxShadow={`0px -1px 32px rgba(50, 50, 71, 0.12), 0px 24px 32px rgba(50, 50, 71, 0.12)`}
      borderRadius={`0px 10px 0px 0px`}
    >
      <FormProvider {...formMethods}>
        {
          contraparte ?
            <Legendas
              naoIniciada="Não sofreu lançamento"
              emAndamento="Lançamento não validado"
              concluida="Lançamento validado"
              encerrado="Encerrado e N/A"
            />
            :
            <Legendas
              naoIniciada="Lançamento não iniciado"
              emAndamento="Lançamento incompleto"
              concluida="Lançamento concluído"
              encerrado="Encerrado e N/A"
            />
        }
        <FilterMovPesquisaFornecedor
          handleSearch={handleSearch}
        />
      </FormProvider>
      {listaMovimentacoes?.map((el) => {
        // LOG 2: DADOS COMPLETOS DE CADA ITEM
        /*console.log('=== DADOS DO ITEM ===', {
          contratoId: el.contratoId,
          statusMovimentacaoFornecedor: el.statusMovimentacaoFornecedor,
          cdContrato: el.cdContrato,
          nmEmpresaFornecedor: el.nmEmpresaFornecedor,
          dtVigenciaInicio: el.dtVigenciaInicio,
          dtVigenciaFim: el.dtVigenciaFim,
          competenciasList: el.competenciasList,
          encerrado: el.encerrado,
          cre: el.cre
        });*/

        const dataCollpase = {
          code: el?.cdContrato,
          dataFim: FORMAT.formatDDMMYYYY(el?.dtVigenciaFim),
          dataIni: FORMAT.formatDDMMYYYY(el?.dtVigenciaInicio),
          empresa: el?.nmEmpresaFornecedor,
          planta: el?.txDescricao,
          titleCollapse: el?.txObjetoContrato,
        }

        //Agora usa o campo encerrado para verificar se o botão deve ser desabilitado
        const buttonDisabled = isButtonDisabled(el?.encerrado, el?.flCompAtual, el?.statusMovimentacaoFornecedor, el?.cre);
        const statusInfo = getStatusColor(el?.statusMovimentacaoFornecedor, buttonDisabled);

        return (
          <ButtonDisabledWrapper key={el?.contratoId?.toString()} isDisabled={buttonDisabled}>
            <CollapseSearchV2
              callBack={() => handleClickCollapse(el)}
              data={dataCollpase}
              isExpanded={handleIsExpanded(el?.contratoId)}
              txtButton="COMPETÊNCIA ATUAL"
              hideButton={false} // Mantém o botão visível
              buttonColor={statusInfo.buttonColor} // Cor cinza quando desabilitado
              borderColor={statusInfo.borderColor} // Borda cinza quando desabilitado
              height='60.14px'
              setExpanded={(expanded) => handleExpanded(expanded, el)}
            >
              <TableMovPesquisaFornecedor
                tableData={el?.competenciasList || []}
                handleClickComp={(competencia) =>
                  handleClickCompAnterior(competencia, el)
                }
              />
            </CollapseSearchV2>
          </ButtonDisabledWrapper>
        )
      })}
    </Card>
  )
}

export default PesquisaMovFornecedor